/**
 * SpotHitch State Management
 * Reactive state store with localStorage persistence
 */

import { Storage } from '../utils/storage.js';

// Initial state
const initialState = {
  // User
  authLoading: true, // true until Firebase Auth resolves (prevents flash of "not connected")
  user: null,
  username: '',
  firstName: '',
  lastName: '',
  birthYear: null,
  gender: '',
  bio: '',
  avatar: 'thumbs-up',
  profilePhotos: [],
  isLoggedIn: false,
  currentUser: null,
  userProfile: null,
  authPendingAction: null,
  showAuthReason: null,

  // UI
  activeTab: 'map',
  viewMode: 'list',
  showWelcome: false,
  theme: 'dark',
  lang: 'fr',
  activeSubTab: 'planner',
  socialSubTab: 'messagerie',
  profileSubTab: 'profil',
  groupSubTab: 'mine',
  eventSubTab: 'upcoming',
  feedFilter: 'all',
  eventFilter: 'all',
  showCompanionSearch: false,
  // Group Conversations (Firebase)
  groupConversations: [],
  activeGroupConversation: null,
  showCreateGroupConversation: false,
  groupConversationSelectedFriends: [],
  groupConversationLoading: false,
  ambassadorSearchQuery: '',
  userOnline: false,
  ambassadorSearchResults: [],
  showAmbassadorSuccess: false,
  ambassadorError: null,
  showAmbassadorProfile: false,
  currentAmbassadorProfile: null,
  showContactAmbassador: false,
  selectedAmbassador: null,
  companionRequests: [],
  companionSearchResults: [],
  sosSession: null,

  // Spots
  spots: [],
  selectedSpot: null,
  searchQuery: '',
  activeFilter: 'all',
  filterCountry: 'all',
  filterMinRating: 0,
  filterMaxWait: 999,

  // Favorites
  favorites: [],
  favoritesSort: 'date',
  filterFavorites: false,
  showFavoritesOnMap: false,

  // Modals
  showAddSpot: false,
  showRating: false,
  showTripPlanner: false,
  showSOS: false,
  showSettings: false,
  showQuiz: false,
  showAuth: false,
  showCompleteProfile: false,
  showFilters: false,
  showStats: false,
  showBadges: false,
  showChallenges: false,
  showShop: false,
  showMyRewards: false,
  showSideMenu: false,
  showIdentityVerification: false,
  showGuardianModal: false,
  showGuidesOverlay: false,
  showOfflinePanel: false,
  guideSection: 'start',
  guideActiveSection: 'laws',

  // Checkin Modal
  checkinSpot: null,
  checkinWaitTime: null,
  checkinWaitSliderIndex: null,
  checkinRideResult: null,
  checkinChars: {},
  checkinPhotoData: null,

  // New badges/challenges
  newBadge: null,
  selectedCountryGuide: null,
  selectedFriendChat: null,

  // Trip Planner
  tripSteps: [],
  activeTrip: null,
  savedTrips: [],
  tripRoute: null,
  tripFrom: '',
  tripTo: '',
  tripResults: null,
  showTripMap: false,
  tripLoading: false,
  showRouteAmenities: false,
  routeAmenities: [],
  loadingRouteAmenities: false,
  searchCountry: null,
  tripBottomSheetState: 'collapsed',
  tripRemovedSpots: [],
  tripShowGasStations: false,
  tripFormCollapsed: false,
  tripGpsPosition: null,
  tripOfflineData: null,
  tripDownloadProgress: null,
  routeFilter: 'all',

  // Gamification
  points: 0,
  level: 1,
  checkins: 0,
  spotsCreated: 0,
  reviewsGiven: 0,
  detailedReviews: [],
  refreshReviews: false,
  showReviewForm: false,
  reviewSpotId: null,
  editingReviewId: null,
  showReplyModal: false,
  replyToReviewId: null,
  badges: [],
  rewards: [],
  skillPoints: 0,
  thumbs: 0,
  lastDailyRewardResult: null,
  dailyRewardProtection: false,
  equippedFrame: null,
  equippedTitle: null,

  // Chat & Social
  messages: [],
  friends: [],
  friendRequests: [],
  friendsLocations: [],
  privateMessages: {},
  unreadFriendMessages: 0,
  dmLastSent: null,
  typingIn: null,
  chatMessages: [],
  forbiddenWords: [],
  showAddForbiddenWordModal: false,

  // Friend Challenges (#157)
  friendChallenges: [],
  activeChallenges: [],
  pendingChallenges: [],

  // Guilds (#162)
  guilds: [],
  myGuildId: null,

  // Season/Leagues
  seasonPoints: 0,
  totalPoints: 0,

  // Anniversary
  showAnniversaryModal: false,
  anniversaryReward: null,
  lastReactionUpdate: null,

  // SOS
  sosActive: false,
  emergencyContacts: [],

  // Location
  userLocation: null,
  gpsEnabled: false,
  showLocationPermission: false,
  locationPermissionChoice: 'unknown', // 'granted' | 'denied' | 'unknown'

  // Tutorial
  showTutorial: false, // Will be set true only if tutorialCompleted is false on first load
  tutorialStep: 0,
  tutorialCompleted: false,

  // Check-in history
  checkinHistory: [],
  checkinHistoryFilter: 'all',
  checkinDisplayLimit: 20,

  // Identity Verification (#190) - Progressive Trust System (0-5)
  verificationLevel: 0, // 0: unverified, 1: email, 2: phone, 3: selfie+ID submitted, 4: ID verified, 5: trusted member
  trustLevel: 0, // Alias for verificationLevel
  pendingPhoneVerification: null,
  pendingPhotoVerification: null,
  pendingIdentityVerification: null,
  pendingSelfieIdVerification: null,
  verifiedPhone: null,
  verifiedPhotoUrl: null,
  identityVerifiedAt: null,
  selfieIdVerifiedAt: null,

  // GPS Navigation
  gasStations: [],
  showGasStationsOnMap: false,
  showMapLegend: false,
  navigationActive: false,
  navigationDestination: null,
  navigationRoute: null,

  // Missing modal states
  showAgeVerification: false,
  showTitles: false,
  showTeamChallenges: false,
  showCreateTeam: false,
  currentTeam: null,
  showJoinTeam: false,
  showTeamSettings: false,
  showNearbyFriends: false,
  showProfileCustomization: false,
  showTripHistory: false,
  selectedTravelPlan: null,
  showTravelPlanDetail: false,
  showPostTravelPlan: false,
  showContactForm: false,
  showPhotoUpload: false,
  photoUploadSpotId: null,
  showTitlePopup: false,
  newTitle: null,
  showSeasonRewards: false,
  showInstallBanner: false,
  showReport: false,
  reportType: null,
  reportTargetId: null,
  selectedReportReason: null,
  showLanding: false,
  showBadgeDetail: false,
  selectedBadgeId: null,
  showBadgePopup: false,
  showLeaderboard: false,
  showMyData: false,
  showDonation: false,
  showDonationThankYou: false,
  donationAmount: null,
  showFriendProfile: false,
  selectedFriendProfileId: null,
  guestProfile: null,
  profileReviews: null,
  myProfileReviews: null,
  showWriteReview: false,
  reviewTargetUid: null,
  friendProfileSocialLinks: null,
  showCreateEvent: false,
  showAdminPanel: false,
  dangerousSpotAlerts: [],
  deletionProposals: [],
  bans: [],
  warnings: [],
  moderationLogs: [],
  showAdminModeration: false,
  adminModerationView: 'queue',
  moderationQueueFilter: 'all',
  moderationReportsPage: 1,
  showAccessibilityHelp: false,
  showDailyReward: false,
  showAddFriend: false,
  showSafety: false,
  showBlockedUsers: false,
  showBlockModal: false,
  blockTargetId: null,
  blockTargetName: null,
  // Consent
  showConsentSettings: false,
  showDeleteAccount: false,

  // Feedback Panel
  showFeedbackPanel: false,
  feedbackActiveTab: 'carte',
  feedbackDetailFeature: null,
  showComingSoonRadar: false,
  showComingSoonCompanion: false,
  showComingSoonCityGuide: false,

  // Voyageurs tab
  voyageursView: 'combined',
  radarEnabled: false,
  nearbyTravelers: [],
  travelBuddies: [],
  selectedBuddyDetail: null,
  buddyCountryFilter: 'all',

  // Admin Dashboard
  adminActiveTab: 'feedback',
  adminFeedbackData: null,
  adminFeedbackPeriod: 'all',
  adminSentryIssues: null,
  adminReportsData: null,

  // AddSpot wizard
  addSpotStep: 1,
  addSpotType: null,
  addSpotWaitTime: null,
  addSpotMethod: null,
  addSpotGroupSize: null,
  addSpotTimeOfDay: null,

  // Validation mode for AddSpot
  addSpotValidateId: null,

  // Drafts
  spotDraftsBannerVisible: false,

  // Additional data keys
  addSpotPreview: false,
  ratingSpotId: null,
  locationPermissionDenied: false,
  selfiePhoto: null,
  idCardPhoto: null,
  selfieWithIdPhoto: null,
  selectedEvent: null,
  eventsLastUpdate: null,
  authMode: 'login',
  currentRating: 0,
  selectedAvatar: null,
  pendingProfileAction: null,
  userName: '',
  userAvatar: '',
  quizActive: false,
  quizResult: null,
  quizCountryCode: null,
  quizShowExplanation: false,
  quizTimeLeft: 0,
  challengeTab: 'active',
  leaderboardTab: 'weekly',
  leaderboardData: null,
  shopCategory: 'all',
  profileFrame: null,
  profileTitle: null,
  selectedCountryCode: null,
  selectedFriendId: null,
  homeSearchLabel: '',
  league: null,
  filterVerifiedOnly: false,
  searchFriendQuery: '',
  referrals: [],
  inviteCode: null,
  inviteShareAttempts: 0,
  invitedUsers: [],
  inviteMilestones: [],
  isPWAInstalled: false,
  unlockedSkins: [],
  dailyRewardStreak: 0,
  dailyRewardsHistory: [],
  lastDailyRewardClaim: null,
  activeDMConversation: null,
  nearbyFriendsEnabled: false,
  nearbyFriendsRadius: 50,
  nearbyNotifications: false,
  shareLocationWithFriends: false,
  travelModeEnabled: false,
  travelModeLocation: null,
  selfieIdStep: 0,
  documentType: null,
  documentPreview: null,
  photoPreview: null,
  legalPage: null,
  showFAQ: false,
  faqSearchQuery: '',
  showLegal: false,
  notifications: true,
  proximityAlerts: true,
  proximityAlertSpot: null,

  // City Panel
  selectedCity: null,
  selectedRoute: null,
  cityData: null,

  // Offline download
  offlineDownloadingCountry: null,
  offlineDownloadProgress: 0,
  offlineAutoDownloadEnabled: true,

  // Guide Nudge (after spot creation)
  pendingGuideCountry: null, // { code, name, flag }
  showGuideNudge: false,

  // Loading states
  isLoading: false,
  isLoadingSpots: false,
  isOnline: navigator.onLine,

  // Internal refresh counters
  _tagRefresh: 0,
  _valRefresh: 0,
};

// State subscribers
const subscribers = new Set();

// Create reactive state
let state = { ...initialState };

// Load persisted state from storage
function loadPersistedState() {
  const persisted = Storage.get('state');
  if (persisted) {
    // Merge persisted state with initial state (to add any new properties)
    state = { ...initialState, ...persisted };

    // CRITICAL: never trust persisted isLoggedIn — Firebase Auth will set it properly
    // (old versions persisted this flag, causing auth gate bypass)
    state.isLoggedIn = false;
    state.currentUser = null;

    // Migrate old users: showWelcome no longer blocks, disable it
    if (state.showWelcome) {
      state.showWelcome = false;
    }
    // Load profile photos from separate storage (too large for main state)
    try {
      const photos = JSON.parse(localStorage.getItem('spothitch_profile_photos') || '[]')
      if (Array.isArray(photos)) state.profilePhotos = photos
    } catch { /* ignore */ }
  } else {
    // Brand new user — tutorial will be shown after landing page dismissal
  }
}

// Save state to storage
function persistState() {
  const stateToPersist = {
    username: state.username,
    firstName: state.firstName,
    lastName: state.lastName,
    birthYear: state.birthYear,
    gender: state.gender,
    bio: state.bio,
    avatar: state.avatar,
    theme: state.theme,
    lang: state.lang,
    showTutorial: state.showTutorial,
    tutorialCompleted: state.tutorialCompleted,
    points: state.points,
    level: state.level,
    checkins: state.checkins,
    spotsCreated: state.spotsCreated,
    reviewsGiven: state.reviewsGiven,
    badges: state.badges,
    rewards: state.rewards,
    savedTrips: state.savedTrips,
    emergencyContacts: state.emergencyContacts, // RGPD: third-party data, included in GDPR export
    favorites: state.favorites,
    favoritesSort: state.favoritesSort,
    checkinHistory: state.checkinHistory,
    verificationLevel: state.verificationLevel,
    trustLevel: state.trustLevel || state.verificationLevel,
    verifiedPhone: state.verifiedPhone,
    identityVerifiedAt: state.identityVerifiedAt,
    selfieIdVerifiedAt: state.selfieIdVerifiedAt,
    messages: (state.messages || []).slice(-100),
    privateMessages: state.privateMessages || {},
    friends: state.friends || [],
    proximityAlerts: state.proximityAlerts,
    filterCountry: state.filterCountry,
    filterMinRating: state.filterMinRating,
    filterMaxWait: state.filterMaxWait,
    filterVerifiedOnly: state.filterVerifiedOnly,
  };
  Storage.set('state', stateToPersist);

  // Sync all local data to Firebase (debounced, won't fire on every call)
  // Cache the module reference after first import to avoid creating new Promise chains
  if (!persistState._fbSyncModule) {
    persistState._fbSyncPromise = persistState._fbSyncPromise || import('../services/firebaseSync.js')
    persistState._fbSyncPromise.then(m => {
      persistState._fbSyncModule = m
      m.syncAllToFirestore()
    }).catch(() => {})
  } else {
    persistState._fbSyncModule.syncAllToFirestore()
  }
}

// Debounced persist — batch rapid setState calls into one localStorage write
let persistScheduled = false
function debouncedPersist() {
  if (persistScheduled) return
  persistScheduled = true
  // Use queueMicrotask to batch all setState calls within the same tick,
  // but persist before the next event loop iteration (keeps tests happy)
  queueMicrotask(() => {
    persistScheduled = false
    persistState()
  })
}

// Notify all subscribers of state change
function notifySubscribers() {
  subscribers.forEach(callback => {
    try {
      callback(state);
    } catch (error) {
      console.error('Subscriber error:', error);
    }
  });
}

/**
 * Get current state (read-only snapshot)
 */
export function getState() {
  return { ...state };
}

/**
 * Update state with partial updates
 * @param {Partial<typeof initialState>} updates - State updates
 */
export function setState(updates) {
  // E2E test hook: prevent Firebase onAuthStateChanged(null) from resetting isLoggedIn
  // during test waits. Only active when window.__e2eAuthLock is set by test helpers.
  if (typeof window !== 'undefined' && window.__e2eAuthLock && updates?.isLoggedIn === false) return

  // Skip render if caller already updated the DOM (fast step swap)
  const skipRender = updates._skipRender
  if (skipRender) delete updates._skipRender

  // Dirty-checking: skip if nothing actually changed
  let changed = false
  for (const key in updates) {
    if (state[key] !== updates[key]) {
      changed = true
      break
    }
  }
  if (!changed) return

  state = { ...state, ...updates }
  if (!skipRender) notifySubscribers()
  debouncedPersist()
}

/**
 * Subscribe to state changes
 * @param {Function} callback - Called when state changes
 * @returns {Function} Unsubscribe function
 */
export function subscribe(callback) {
  subscribers.add(callback);
  // Call immediately with current state
  callback(state);
  // Return unsubscribe function
  return () => subscribers.delete(callback);
}

/**
 * Reset state to initial values
 */
export function resetState() {
  state = { ...initialState };
  Storage.remove('state');
  notifySubscribers();
}

// Actions
export const actions = {
  // Navigation
  changeTab(tab) {
    setState({ activeTab: tab, selectedSpot: null });
  },

  // Theme
  toggleTheme() {
    const newTheme = state.theme === 'dark' ? 'light' : 'dark';
    setState({ theme: newTheme });
    document.body.classList.toggle('light-theme', newTheme === 'light');
  },

  // Language
  setLanguage(lang) {
    setState({ lang });
  },

  // Spots
  setSpots(spots) {
    setState({ spots, isLoadingSpots: false });
  },

  selectSpot(spot) {
    setState({ selectedSpot: spot });
  },

  setFilter(filter) {
    setState({ activeFilter: filter });
  },

  setSearchQuery(query) {
    setState({ searchQuery: query });
  },

  // User
  setUser(user) {
    setState({
      user,
      isLoggedIn: !!user,
      username: user?.displayName || state.username,
    });
  },

  updateProfile(updates) {
    setState({
      username: updates.username || state.username,
      avatar: updates.avatar || state.avatar,
    });
  },

  // Gamification
  addPoints(amount) {
    const newPoints = state.points + amount;
    const newLevel = Math.floor(newPoints / 100) + 1;
    setState({
      points: newPoints,
      level: Math.max(state.level, newLevel),
    });
  },

  incrementCheckins() {
    setState({
      checkins: state.checkins + 1,
    });
    actions.addPoints(5);
  },

  incrementSpotsCreated() {
    setState({
      spotsCreated: state.spotsCreated + 1,
    });
    actions.addPoints(20);
  },

  incrementReviews() {
    setState({
      reviewsGiven: state.reviewsGiven + 1,
    });
    actions.addPoints(10);
  },

  addBadge(badge) {
    if (!state.badges.includes(badge)) {
      setState({
        badges: [...state.badges, badge],
      });
      actions.addPoints(50);
    }
  },

  // Trips
  setTripSteps(steps) {
    setState({ tripSteps: steps });
  },

  saveTrip(trip) {
    setState({
      savedTrips: [...state.savedTrips, trip],
    });
  },

  // SOS
  toggleSOS() {
    setState({ sosActive: !state.sosActive });
  },

  addEmergencyContact(contact) {
    setState({
      emergencyContacts: [...state.emergencyContacts, contact],
    });
  },

  // Tutorial
  nextTutorialStep() {
    if (state.tutorialStep < 2) {
      setState({ tutorialStep: state.tutorialStep + 1 });
    } else {
      setState({ showTutorial: false, tutorialStep: 0 });
    }
  },

  prevTutorialStep() {
    if (state.tutorialStep > 0) {
      setState({ tutorialStep: state.tutorialStep - 1 });
    }
  },

  skipTutorial() {
    setState({ showTutorial: false, tutorialStep: 0 });
  },

  // Location
  setUserLocation(location) {
    setState({
      userLocation: location,
      gpsEnabled: !!location,
    });
    // Record in location history for proximity verification
    if (location?.lat && location?.lng) {
      import('../services/proximityVerification.js').then(({ recordLocation }) => {
        recordLocation(location)
      }).catch(() => {})
    }
  },

  // Online status
  setOnlineStatus(isOnline) {
    setState({ isOnline });
  },

  // Check-in history
  addCheckinToHistory(checkinData) {
    const checkinHistory = state.checkinHistory || [];
    const newCheckin = {
      id: `checkin_${Date.now()}_${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`,
      ...checkinData,
      timestamp: checkinData.timestamp || new Date().toISOString(),
    };
    setState({
      checkinHistory: [newCheckin, ...checkinHistory],
    });
    return newCheckin;
  },
};

// Initialize state on load
loadPersistedState();

// Apply theme on load (check immediate override first, then persisted state)
try {
  const themeOverride = localStorage.getItem('spothitch_theme_override')
  if (themeOverride && (themeOverride === 'light' || themeOverride === 'dark')) {
    state = { ...state, theme: themeOverride }
    localStorage.removeItem('spothitch_theme_override')
  }
} catch { /* no-op */ }
if (state.theme === 'light') {
  document.body.classList.add('light-theme');
}

// Online/offline state is managed centrally by network.js initNetworkMonitor()
// — removed duplicate listeners here to avoid race conditions (6 handlers were fighting)

// Export default state object for backward compatibility
export default state;
